<template>
    <OverlayLoader :isLoading="!feedLoaded || !profilesLoaded">
        <main class="page page-dashboard">
            <div class="container">

                <PageHeader
                    :title="$translate.text('clients.feeds.edit')"
                />

                <form @submit.prevent="submitForm" method="POST">
                    <h4>{{ t('clients.feeds.general') }}</h4>

                    <TextInput
                        id="name"
                        :value="feed.name"
                        name="name"
                        @change="feed.name = $event"
                        :label="$translate.text('clients.feeds.name')"
                        :required="true"
                    />

                    <Select
                        id="profile"
                        :value="feed.parent_id"
                        name="profile"
                        @change="$event !== '' ? feed.parent_id = $event : feed.parent_id = null"
                        :options="profileOptions"
                        :label="$translate.text('general.profile')"
                        :required="false"
                    />

                    <Select
                        id="type"
                        :value="feed.type"
                        name="type"
                        @change="feed.type = $event"
                        :options="typeOptions"
                        :label="$translate.text('clients.feeds.type')"
                        :required="true"
                    />

                    <Select
                        v-if="feed.parent_id == null"
                        id="layout"
                        :value="feed.layout"
                        name="comparePrices"
                        @change="feed.layout = $event"
                        :options="layoutOptions"
                        :label="$translate.text('clients.feeds.layout')"
                        :required="true"
                    />

                    <TextInput
                        v-if="feed.type !== 'FILE'"
                        id="host"
                        :value="feed.host"
                        name="host"
                        @change="feed.host = $event"
                        :label="$translate.text('clients.feeds.host')"
                        :required="true"
                    />

                    <TextInput
                        v-if="feed.type !== 'FILE' && extraFilesAllowed"
                        id="host_extra"
                        :value="feed.host_extra"
                        name="host_extra"
                        @change="feed.host_extra = $event"
                        :label="$translate.text('clients.feeds.host_extra')"
                        :required="false"
                    />

                    <TextInput
                        v-if="feed.type !== 'FILE' && extraFilesAllowed"
                        id="host_extra2"
                        :value="feed.host_extra2"
                        name="host_extr2a"
                        @change="feed.host_extra2 = $event"
                        :label="$translate.text('clients.feeds.host_extra')"
                        :required="false"
                    />

                    <YesNoInput
                        v-if="feed.type !== 'FILE'"
                        id="authenticationRequired"
                        :value="feed.authentication_required ? 'yes' : 'no'"
                        name="authenticationRequired"
                        @change="feed.authentication_required = $event === 'yes'"
                        :label="$translate.text('clients.feeds.authentication_required')"
                    />

                    <TextInput
                        v-if="feed.authentication_required === true && feed.type !== 'FILE'"
                        id="authenticationUsername"
                        :value="feed.authentication_user"
                        name="authenticationUsername"
                        @change="feed.authentication_user = $event"
                        :label="$translate.text('clients.feeds.authentication_user')"
                        :required="feed.authentication_required === true"
                    />

                    <PasswordInput
                        v-if="feed.authentication_required === true && feed.type !== 'FILE'"
                        id="authenticationPassword"
                        :value="feed.authentication_password"
                        name="authenticationPassword"
                        @change="feed.authentication_password = $event"
                        :label="$translate.text('clients.feeds.authentication_password')"
                        :required="feed.authentication_required === true"
                    />

                    <h4 v-if="feed.type === 'FTP'">{{ t('clients.feeds.ftp_settings') }}</h4>

                    <TextInput
                        v-if="feed.type === 'FTP'"
                        id="path"
                        :value="feed.ftp_dir_path"
                        name="path"
                        @change="feed.ftp_dir_path = $event"
                        :label="$translate.text('clients.feeds.path')"
                        :required="feed.type === 'FTP'"
                    />

                    <TextInput
                        v-if="feed.type === 'FTP'"
                        id="fileName"
                        :value="feed.ftp_filename"
                        name="fileName"
                        @change="feed.ftp_filename = $event"
                        :label="$translate.text('clients.feeds.file_name')"
                        :required="feed.type === 'FTP'"
                    />

                    <h4 v-if="feed.type === 'FILE'">{{ t('clients.feeds.file_upload_settings') }}</h4>

                    <UploadInput
                        v-if="feed.type === 'FILE'"
                        id="fileUpload"
                        :value="feed.file.name"
                        name="fileUpload"
                        @change="feed.file = $event"
                        @download="downloadFile('file', feed.file.name)"
                        :uploadPercentage ="uploadPercentage"
                        :label="$translate.text('clients.feeds.choose_file')"
                    />

                    <UploadInput
                        v-if="feed.type === 'FILE' && extraFilesAllowed"
                        id="fileUploadExtra"
                        :value="feed.file_extra.name"
                        name="fileUploadExtra"
                        @change="feed.file_extra = $event"
                        @download="downloadFile('file_extra', feed.file_extra.name)"
                        :uploadPercentage ="uploadPercentage"
                        :label="$translate.text('clients.feeds.choose_extra_file')"
                    />

                    <UploadInput
                        v-if="feed.type === 'FILE' && extraFilesAllowed"
                        id="fileUploadExtra2"
                        :value="feed.file_extra2.name"
                        name="fileUploadExtra2"
                        @change="feed.file_extra2 = $event"
                        @download="downloadFile('file_extra2', feed.file_extra2.name)"
                        :uploadPercentage ="uploadPercentage"
                        :label="$translate.text('clients.feeds.choose_extra_file')"
                    />

                    <h4 v-if="feed.layout === 'XML' && feed.parent_id == null">{{ t('clients.feeds.xml_settings') }}</h4>

                    <TextInput
                        v-if="feed.layout === 'XML' && feed.parent_id == null"
                        id="productNodeName"
                        :value="feed.xml_node"
                        name="productNodeName"
                        @change="feed.xml_node = $event"
                        :label="$translate.text('clients.feeds.product_node_name')"
                        :required="feed.layout === 'XML'"
                    />

                    <h4 v-if="feed.layout === 'CSV' && feed.parent_id == null">{{ t('clients.feeds.csv_settings') }}</h4>

                    <Select
                        v-if="feed.layout === 'CSV' && feed.parent_id == null"
                        id="seperator"
                        :value="feed.csv_seperator"
                        name="seperator"
                        @change="feed.csv_seperator = $event"
                        :options="seperatorOptions"
                        :label="$translate.text('clients.feeds.seperator')"
                        :required="feed.layout === 'CSV'"
                    />

                    <Select
                        v-if="feed.layout === 'CSV' && feed.parent_id == null"
                        id="wordSeperator"
                        :value="feed.csv_word_seperator"
                        name="wordSeperator"
                        @change="feed.csv_word_seperator = $event"
                        :options="wordSeperatorOptions"
                        :label="$translate.text('clients.feeds.word_seperator')"
                        :required="feed.layout === 'CSV'"
                    />

                    <!-- <NumberInput
                        v-if="feed.layout === 'CSV'"
                        id="rowsToIgnore"
                        :value="feed.csv_rows_ignore"
                        name="rowsToIgnore"
                        @change="feed.csv_rows_ignore = $event"
                        :label="$translate.text('clients.feeds.rows_ignore')"
                    /> -->

                    <YesNoInput
                        v-if="feed.layout === 'CSV' && feed.parent_id == null"
                        id="firstRowsAreColumnNames"
                        :value="feed.csv_first_row_names ? 'yes' : 'no'"
                        name="firstRowsAreColumnNames"
                        @change="feed.csv_first_row_names = $event === 'yes'"
                        :label="$translate.text('clients.feeds.first_row_names')"
                    />

                    <h4>{{ t('clients.feeds.ftp_images_settings') }}</h4>

                    <YesNoInput
                        id="ftpImagesHost"
                        :value="feed.ftp_images ? 'yes' : 'no'"
                        name="ftpImagesHost"
                        @change="feed.ftp_images = $event === 'yes'"
                        :label="$translate.text('clients.feeds.ftp_download_images')"
                    />

                    <TextInput
                        v-if="feed.ftp_images === true"
                        id="host"
                        :value="feed.ftp_images_host"
                        name="host"
                        @change="feed.ftp_images_host = $event"
                        :label="$translate.text('clients.feeds.host')"
                        :required="true"
                    />

                    <TextInput
                        v-if="feed.ftp_images === true"
                        id="ftpImagesAuthenticationUsername"
                        :value="feed.ftp_images_authentication_username"
                        name="ftpImagesAuthenticationUsername"
                        @change="feed.ftp_images_authentication_username = $event"
                        :label="$translate.text('clients.feeds.authentication_user')"
                    />

                    <PasswordInput
                        v-if="feed.ftp_images === true"
                        id="ftpImagesAuthenticationPassword"
                        :value="feed.ftp_images_authentication_password"
                        name="ftpImagesAuthenticationPassword"
                        @change="feed.ftp_images_authentication_password = $event"
                        :label="$translate.text('clients.feeds.authentication_password')"
                        :required="feed.authentication_required === true"
                    />

                    <TextInput
                        v-if="feed.ftp_images === true"
                        id="ftpImagesDirPath"
                        :value="feed.ftp_images_dir_path"
                        name="ftpImagesDirPath"
                        @change="feed.ftp_images_dir_path = $event"
                        :label="$translate.text('clients.feeds.path')"
                        :required="true"
                    />

                    <h4 v-if="feed.parent_id == null">{{ t('clients.feeds.settings') }}</h4>

                    <Select
                        v-if="feed.parent_id == null"
                        id="notation"
                        :value="feed.price_decimal_delimiter"
                        name="notation"
                        @change="feed.price_decimal_delimiter = $event"
                        :options="notationOptions"
                        :label="$translate.text('clients.feeds.notation')"
                        :required="true"
                        :tooltip="true"
                        :tooltip_text="$translate.text('clients.notation.explanation')"
                    />


                    <FormSubmit
                        :routeAfterCancelLink="{ name: 'Clients Feeds' }"
                        :buttonLabel="submittingIsLoading ? $translate.text('general.save_changes.saving') :  $translate.text('general.save_changes')"
                        :disabled="submittingIsLoading"
                    />
                </form>
            </div>
        </main>
    </OverlayLoader>
</template>
<script>
import axios from 'axios';

import { get } from '@/helpers/http';
import handleError from '@/helpers/error';

import PageHeader from '@/components/PageHeader/PageHeader';
import TextInput from '@/components/Inputs/TextInput';
// import NumberInput from '@/components/Inputs/NumberInput';
import PasswordInput from '@/components/Inputs/PasswordInput';
import YesNoInput from '@/components/Inputs/YesNoInput';
import Select from '@/components/Inputs/Select';
import FormSubmit from '@/components/Forms/FormSubmit';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import UploadInput from '@/components/Inputs/UploadInput';

import { getHeaders, getToken } from '@/helpers/auth';

export default {
    name: 'EditFeed',

    components: {
        UploadInput,
        PageHeader,
        TextInput,
        // NumberInput,
        PasswordInput,
        YesNoInput,
        Select,
        FormSubmit,
        OverlayLoader,
    },

    data() {
        return {
            feed: {
                name: '',
                parent_id: null,
                active: true,
                type: '',
                layout: '',
                host: '',
                host_extra: null,
                host_extra2: null,
                authentication_required: false,
                authentication_user: '',
                authentication_password: '',

                file: {
                    name: '',
                },

                file_extra: {
                    name: '',
                },

                file_extra2: {
                    name: '',
                },

                csv_seperator: '',
                csv_word_seperator: '',
                csv_rows_ignore: 0,
                csv_first_row_names: false,

                xml_node: 'product',

                ftp_dir_path: '',
                ftp_filename: '',

                ftp_images: false,
                ftp_images_host: null,
                ftp_images_authentication_username: null,
                ftp_images_authentication_password: null,
                ftp_images_dir_path: '/',

                price_decimal_delimiter: null,
            },

            typeOptions: [
                { label: this.$translate.text('general.select_option'), value: null },
                { label: 'HTTP(S)', value: 'HTTP' },
                { label: 'FTP(S)', value: 'FTP' },
                { label: 'Upload File', value: 'FILE' },
            ],

            layoutOptions: [
                { label: this.$translate.text('general.select_option'), value: null },
                { label: 'XML', value: 'XML' },
                { label: 'CSV', value: 'CSV' },
            ],

            authenticationRequiredOptions: [
                { label: this.$translate.text('general.no'), value: false },
                { label: this.$translate.text('general.yes'), value: true },
            ],

            seperatorOptions: [
                { label: this.$translate.text('general.select_option'), value: null },
                { label: this.$translate.text('general.comma'), value: 'Comma' },
                { label: this.$translate.text('general.dotcomma'), value: 'Dotcomma' },
                { label: this.$translate.text('general.tab'), value: 'Tab' },
                { label: this.$translate.text('general.pipe'), value: 'Pipe' },
            ],

            wordSeperatorOptions: [
                { label: this.$translate.text('general.select_option'), value: null },
                { label: this.$translate.text('general.double.quote'), value: 'Double quote' },
                { label: this.$translate.text('general.single.quote'), value: 'Single quote' },
            ],

            notationOptions: [
                { label: this.$translate.text('general.select_option'), value: null, disabled: true },
                { label: this.$translate.text('general.comma'), value: ',' },
                { label: this.$translate.text('general.dot'), value: '.' },
            ],

            profileOptions: [
                { label: this.$translate.text('general.none'), value: null, extra_files_allowed: false },
            ],

            profilesLoaded: false,

            submittingIsLoading: false,
            feedLoaded: false,

            uploadPercentage: 0,
        };
    },

    created() {
        this.getFeed();
        this.getProfiles();
    },

    methods: {
        getFeed() {
            get({
                url: `${this.$API_URL}/api/clients/feeds/${this.$route.params.id}`,
            })
                .then((response) => {
                    this.feed = response.data.data;

                    this.feed.authentication_required = this.feed.authentication_required === 1;
                    this.feed.first_row_names = this.feed.first_row_names === 1;
                    this.feed.ftp_images = this.feed.ftp_images === 1;

                    this.feedLoaded = true;
                })
                .catch((error) => {
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        },

        getProfiles() {
            get({
                url: `${this.$API_URL}/api/clients/feeds/profiles`,
            })
                .then((response) => {
                    response.data.data.forEach(profile => {
                        this.profileOptions.push(
                            { label: profile.name, value: profile.id, extra_files_allowed: profile.extra_files_allowed },
                        );
                    });

                    this.profilesLoaded = true;
                })
                .catch((error) => {
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        },

        submitForm() {
            this.submittingIsLoading = true;

            const data = { ...this.feed };

            data.active = data.active ? 1 : 0;
            data.authentication_required = data.authentication_required ? 1 : 0;
            data.csv_first_row_names = data.csv_first_row_names ? 1 : 0;
            data.ftp_images = data.ftp_images ? 1 : 0;

            const headers = getHeaders(getToken());
            headers['Content-Type'] = 'multipart/form-data';

            // FormData converts NULL type to string, remove parent_id if it's null
            if (data.parent_id === null) {
                delete data.parent_id;
            }

            // delete the file_extra when there is no file selected
            if (data.file_extra.name === '') {
                delete data.file_extra;
            }

            // delete the file_extra when there is no file selected
            if (data.file_extra2.name === '') {
                delete data.file_extra2;
            }

            if (data.ftp_images_host === '') {
                delete data.ftp_images_host;
            }

            if (data.ftp_images_authentication_username === '') {
                delete data.ftp_images_authentication_username;
            }

            if (data.ftp_images_authentication_password === '') {
                delete data.ftp_images_authentication_password;
            }

            if (data.ftp_images_dir_path === '') {
                delete data.ftp_images_dir_path;
            }

            const formdata = new FormData();
            Object.keys(data).forEach(key => {
                formdata.append(key, data[key]);
            });

            // TODO: move this function to the API SDK
            axios({
                method: 'POST',
                url: `${this.$API_URL}/api/clients/feeds/${this.feed.id}`,
                headers,
                data: formdata,
                // eslint-disable-next-line
                onUploadProgress: function (progressEvent) {
                    // eslint-disable-next-line
                    this.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                }.bind(this),
            })
                .then(() => {
                    this.submittingIsLoading = false;
                    this.$router.push({ name: 'Clients Feeds' });
                    this.resetForm();
                })
                .catch((error) => {
                    this.submittingIsLoading = false;
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        },

        resetForm() {
            this.feed = {
                name: '',
                parent_id: null,
                active: true,
                type: '',
                layout: '',
                host: '',
                host_extra: null,
                host_extra2: null,
                authentication_required: false,
                authentication_user: '',
                authentication_password: '',

                file: '',

                file_extra: {
                    name: '',
                },

                file_extra2: {
                    name: '',
                },

                csv_seperator: '',
                csv_word_seperator: '',
                csv_rows_ignore: 0,
                csv_first_row_names: false,

                xml_node: 'product',

                ftp_dir_path: '',
                ftp_filename: '',

                ftp_images: false,
                ftp_images_host: null,
                ftp_images_authentication_username: null,
                ftp_images_authentication_password: null,
                ftp_images_dir_path: null,

                price_decimal_delimiter: null,
            };
        },

        downloadFile(type, filename) {
            this.API.clients.feeds.download(this.feed.id, { type: type })
                .then((response) => {
                    // TODO: how to handle the custom header with the mimetype and filename here?
                    const blob = new Blob([response.data], {
                        type: 'text/plain',
                    });

                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = filename;
                    link.click();
                })
                .catch((error) => {
                    handleError({
                        error,
                        message: this.$translate.text('alert.whoops'),
                    });
                });
        }
    },

    computed: {
        extraFilesAllowed: function () {
            if (this.feed.parent_id !== null) {
                const activeProfile = this.profileOptions.find(profile => profile.value == this.feed.parent_id);

                console.log(this.feed.parent_id, activeProfile, this.profileOptions);

                return activeProfile ? activeProfile.extra_files_allowed : false;
            }

            return false;
        },
    },

    watch: {
        'feed.type': function (type) {
            if (type === 'FILE') {
                this.feed.authentication_required = false;
            }
        },
    },
};
</script>
<style lang="scss" scoped>
</style>
